import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { useSelector, useDispatch } from 'react-redux';
import { ClipLoader } from 'react-spinners';
import { useTranslation } from 'react-i18next';

import { tokenizeNotificationMessage } from '../../../utils/notificationLinkify';

import {
  CalendarIcon,
  ChatBubbleLeftEllipsisIcon,
  CheckCircleIcon,
  CheckIcon,
  ClockIcon,
  CurrencyEuroIcon,
  EnvelopeIcon,
  EllipsisVerticalIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
  BellIcon,
} from '@heroicons/react/24/outline';

import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotification,
  getUnreadNotificationsCount,
} from '../../../redux/actions/notifications';

import {
  approveBooking,
  getBooking,
  rejectBooking,
} from '../../../redux/actions/bookings';
import {
  canReviewBooking,
  getEffectiveBookingStatus,
} from '../../../utils/bookingActionability';
import BookingReviewModal from 'components/BookingReviewModal';

/* ───────────────────────────── small UI helpers ───────────────────────────── */

const Eyebrow = ({ children }) => (
  <div className="text-[11px] uppercase tracking-[0.14em] text-muted font-bold mb-1.5">
    {children}
  </div>
);

const TabHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 md:gap-6 mb-6 pb-5 border-b border-line">
    <div className="min-w-0">
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2 className="text-[26px] sm:text-[30px] md:text-[34px] leading-[1.05] tracking-tight text-ink-strong font-bold break-words">
        {title}
      </h2>
      {sub && (
        <p className="text-[14px] text-muted mt-2 max-w-xl leading-relaxed">
          {sub}
        </p>
      )}
    </div>
    {actions && (
      <div className="flex items-center gap-2 flex-wrap">{actions}</div>
    )}
  </div>
);

const Card = ({ children, className = '', padded = true }) => (
  <section
    className={`bg-white border border-line rounded-2xl shadow-softer ${
      padded ? 'p-6' : ''
    } ${className}`}
  >
    {children}
  </section>
);

/* ────────────────────── notification type → icon + accent ─────────────────── */

const ACCENT_CLASSES = {
  rose: { bg: 'bg-rose-50', text: 'text-rose-700' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-800' },
  sky: { bg: 'bg-sky-50', text: 'text-sky-700' },
  neutral: { bg: 'bg-paper-2', text: 'text-ink' },
};

const typeVisual = (type, effectiveStatus) => {
  // Effective status takes precedence (paid/approved/rejected/expired)
  if (effectiveStatus === 'paid')
    return { icon: CurrencyEuroIcon, accent: 'emerald' };
  if (effectiveStatus === 'approved')
    return { icon: CheckCircleIcon, accent: 'emerald' };
  if (effectiveStatus === 'rejected')
    return { icon: XCircleIcon, accent: 'rose' };
  if (effectiveStatus === 'expired')
    return { icon: ClockIcon, accent: 'neutral' };

  switch (type) {
    case 'newBooking':
      return { icon: CalendarIcon, accent: 'amber' };
    case 'approved':
      return { icon: CheckCircleIcon, accent: 'emerald' };
    case 'rejected':
      return { icon: XCircleIcon, accent: 'rose' };
    case 'newUpdate':
      return { icon: ChatBubbleLeftEllipsisIcon, accent: 'sky' };
    case 'brandInvited':
    case 'invite':
    case 'invited':
      return { icon: EnvelopeIcon, accent: 'amber' };
    case 'paymentConfirmed':
      return { icon: CurrencyEuroIcon, accent: 'emerald' };
    case 'expired':
      return { icon: ClockIcon, accent: 'neutral' };
    // Event cancelled — sent to every brand holding a place on it.
    case 'cancelled':
      return { icon: XCircleIcon, accent: 'rose' };
    default:
      return { icon: InformationCircleIcon, accent: 'neutral' };
  }
};

/* ───────────────────────────── status pill ────────────────────────────────── */

const StatusPill = ({ status, t }) => {
  if (!status) return null;
  const s = String(status).toLowerCase();
  const map = {
    approved: {
      color: 'emerald',
      label: t('dashboard.notif_approved'),
      Icon: CheckIcon,
    },
    paid: { color: 'emerald', label: t('dashboard.notif_paid'), Icon: CheckIcon },
    rejected: {
      color: 'rose',
      label: t('dashboard.notif_rejected'),
      Icon: XMarkIcon,
    },
    expired: {
      color: 'neutral',
      label: t('dashboard.notif_expired'),
      Icon: ClockIcon,
    },
  };
  const cfg = map[s] || { color: 'neutral', label: status };
  const c = ACCENT_CLASSES[cfg.color] || ACCENT_CLASSES.neutral;
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full text-[11.5px] font-bold px-2.5 py-0.5 ${c.bg} ${c.text}`}
    >
      {cfg.Icon ? (
        <cfg.Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
      ) : (
        <span className="w-1.5 h-1.5 rounded-full bg-muted" />
      )}
      {cfg.label}
    </span>
  );
};

const ExpiredNotice = ({ when, t }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full text-[11.5px] font-bold px-2.5 py-0.5 bg-paper-2 text-ink">
    <ClockIcon className="w-3.5 h-3.5" />
    {when
      ? t('dashboard.notif_expired_ago', { time: moment(when).fromNow() })
      : t('dashboard.notif_expired')}
  </span>
);

/* ───────────────────────────── notification row ───────────────────────────── */

const NotificationItem = ({
  id,
  type,
  message,
  createdAt,
  updatedAt,
  read,
  bookingId,
  bookingStatus,
  expireAt,
  payload,
  liveBooking,
  isCheckingBooking,
  isBookingStateVerified,
  isBrandUser,
  onMarkRead,
  onDelete,
  onViewDetails,
  onPay,
  onViewEvent,
  t,
  isLast,
}) => {
  const meta = payload || {};
  const [showEventMessageDetails, setShowEventMessageDetails] = useState(false);

  // Translated body (falls back to backend message)
  const normalizedType =
    type === 'invite' || type === 'invited' ? 'brandInvited' : type;
  const msgKey =
    normalizedType === 'expired'
      ? isBrandUser
        ? 'dashboard.notif_msg_expired_brand'
        : 'dashboard.notif_msg_expired_promoter'
      : `dashboard.notif_msg_${normalizedType}`;
  const translatedMessage =
    t(msgKey, {
      brandName: meta?.brandName || '',
      eventName: meta?.eventName || '',
      defaultValue: '',
    }) || message;

  const actionBooking = {
    ...meta,
    ...(liveBooking || {}),
    status:
      liveBooking?.status ||
      liveBooking?.bookingStatus ||
      liveBooking?.effectiveStatus ||
      bookingStatus ||
      meta?.bookingStatus ||
      meta?.status,
    bookingStatus:
      liveBooking?.bookingStatus || bookingStatus || meta?.bookingStatus,
    paymentStatus: liveBooking?.paymentStatus || meta?.paymentStatus,
    expireAt:
      liveBooking?.expireAt ||
      liveBooking?.expiresAt ||
      expireAt ||
      meta?.expireAt ||
      meta?.expiresAt,
  };
  const effectiveBookingStatus = getEffectiveBookingStatus(actionBooking);
  const bookingStatusLower = String(effectiveBookingStatus || '').toLowerCase();

  const isPaid = bookingStatusLower === 'paid';
  const isApproved = bookingStatusLower === 'approved';
  const isRejected = bookingStatusLower === 'rejected';
  const isExpired = bookingStatusLower === 'expired';
  const isNewBooking = type === 'newBooking';
  const isPendingBooking = bookingStatusLower === 'pending';
  const isInvite =
    type === 'brandInvited' || type === 'invite' || type === 'invited';
  const canViewInvitedEvent = Boolean(meta?.eventId);

  const effectiveStatus = isExpired
    ? 'expired'
    : isPaid
      ? 'paid'
      : isApproved
        ? 'approved'
        : isRejected
          ? 'rejected'
          : type === 'approved'
            ? 'approved'
            : type === 'rejected'
              ? 'rejected'
              : null;

  const effectiveStatusAt = isExpired
    ? updatedAt || expireAt || createdAt
    : isPaid || isApproved || isRejected
      ? updatedAt || createdAt
      : type === 'approved' || type === 'rejected'
        ? createdAt
        : !isPendingBooking
          ? updatedAt || createdAt
          : null;

  const showViewDetails =
    !isBrandUser &&
    isNewBooking &&
    !isCheckingBooking &&
    isBookingStateVerified &&
    canReviewBooking(actionBooking);

  const showPayNow =
    isBrandUser && type === 'approved' && !isExpired && !isPaid && !isRejected;

  const showViewEvent = isBrandUser && isInvite && canViewInvitedEvent;
  const eventMessageContent = String(meta?.eventMessageContent || '').trim();
  const hasEventMessageDetails = type === 'newUpdate' && !!eventMessageContent;

  const renderMessage = (msg, p) => {
    const tokens = tokenizeNotificationMessage(msg, {
      brandName: p?.brandName,
      eventName: p?.eventName,
    });
    return tokens.map((tok, i) => {
      if (tok.type === 'brand' && p?.brandId) {
        return (
          <Link
            key={i}
            to={`/brands/${p.brandSlug}`}
            className="text-yellow-03 hover:underline font-semibold"
          >
            {tok.value}
          </Link>
        );
      }
      if (tok.type === 'event' && p?.eventId) {
        return (
          <Link
            key={i}
            to={`/event/${p.eventSlug}`}
            className="text-yellow-03 hover:underline font-semibold"
          >
            {tok.value}
          </Link>
        );
      }
      return <span key={i}>{tok.value}</span>;
    });
  };

  const { icon: Icon, accent } = typeVisual(type, effectiveStatus);
  const accentCls = ACCENT_CLASSES[accent] || ACCENT_CLASSES.neutral;

  return (
    <li
      className={`flex items-start gap-4 px-5 sm:px-6 py-5 transition-colors group ${
        !isLast ? 'border-b border-line/70' : ''
      } ${
        !read
          ? 'bg-brand-soft/70 hover:bg-brand-soft'
          : 'hover:bg-paper-2'
      }`}
    >
      {/* Icon badge */}
      <div
        className={`w-10 h-10 rounded-xl ${accentCls.bg} ${accentCls.text} flex items-center justify-center shrink-0`}
      >
        <Icon className="w-[18px] h-[18px]" />
      </div>

      {/* Body */}
      <div className="flex-1 min-w-0">
        {/* Main message — the translated message itself is the primary content */}
        <div className="flex items-start gap-2">
          <p
            className={`text-[14.5px] leading-snug break-words flex-1 min-w-0 ${
              !read ? 'font-bold text-ink-strong' : 'font-normal text-ink'
            }`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {renderMessage(translatedMessage, meta)}
          </p>
          {!read && (
            <span
              className="mt-1.5 w-1.5 h-1.5 rounded-full bg-yellow-03 shrink-0"
              aria-label="unread"
            />
          )}
        </div>

        {/* Meta row — time ago · status pill · status date */}
        <div className="mt-1.5 flex items-center gap-2.5 flex-wrap text-[12.5px] text-muted">
          <span>{moment(createdAt).fromNow()}</span>
          {effectiveStatus && <StatusPill status={effectiveStatus} t={t} />}
          {effectiveStatus && effectiveStatusAt && (
            <span>
              {t('dashboard.notif_status_date', {
                date: moment(effectiveStatusAt).format('D MMM YYYY'),
                time: moment(effectiveStatusAt).format('HH:mm'),
              })}
            </span>
          )}
          {isNewBooking && isExpired && !showViewDetails && (
            <ExpiredNotice when={updatedAt || expireAt} t={t} />
          )}
          {isBrandUser && type === 'approved' && isExpired && !showPayNow && (
            <ExpiredNotice when={updatedAt || expireAt} t={t} />
          )}
        </div>

        {/* Expandable details for event updates */}
        {hasEventMessageDetails && (
          <div className="mt-2.5">
            <button
              type="button"
              onClick={() => {
                if (!read) onMarkRead?.(id);
                setShowEventMessageDetails((prev) => !prev);
              }}
              className="text-[12.5px] font-bold text-yellow-03 hover:underline"
            >
              {showEventMessageDetails
                ? t('dashboard.notif_hide_details')
                : t('dashboard.notif_see_details')}
            </button>
            {showEventMessageDetails && (
              <div className="mt-2 rounded-xl bg-brand-softer/60 border border-line px-3 py-2.5">
                <p className="text-[13px] text-ink whitespace-pre-line break-words">
                  {eventMessageContent}
                </p>
              </div>
            )}
          </div>
        )}

        {/* CTAs row */}
        {(showViewDetails ||
          showViewEvent ||
          showPayNow ||
          (isNewBooking && isCheckingBooking)) && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            {isNewBooking && isCheckingBooking && (
              <ClipLoader color="#FFBD59" size={16} />
            )}
            {showViewDetails && (
              <button
                onClick={() =>
                  onViewDetails({
                    payload: meta,
                    bookingId,
                    notificationId: id,
                    bookingStatus: effectiveBookingStatus,
                  })
                }
                className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer transition-colors"
              >
                {t('dashboard.notif_view_details')}
              </button>
            )}
            {showViewEvent && (
              <button
                onClick={() => {
                  onMarkRead?.(id);
                  onViewEvent(meta);
                }}
                className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full bg-brand hover:bg-brand-hover text-ink-strong text-[13px] font-bold shadow-softer transition-colors"
              >
                {t('dashboard.notif_view_event')}
              </button>
            )}
            {showPayNow && (
              <button
                onClick={() => onPay(meta)}
                className="inline-flex items-center justify-center gap-1.5 h-9 px-4 rounded-full bg-ink-strong hover:bg-ink-2 text-white text-[13px] font-bold shadow-softer transition-colors"
              >
                {t('dashboard.notif_pay')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Options menu */}
      <div className="shrink-0">
        <Menu as="div" className="relative inline-block text-left">
          <MenuButton
            aria-label="Options"
            className="flex items-center justify-center rounded-full hover:bg-paper-2 w-9 h-9 transition opacity-50 group-hover:opacity-100 focus:opacity-100"
          >
            <EllipsisVerticalIcon className="w-5 h-5 text-muted" />
          </MenuButton>
          <MenuItems className="absolute right-0 mt-2 w-44 rounded-xl bg-white shadow-card border border-line focus:outline-none z-20 overflow-hidden">
            {!read && (
              <MenuItem>
                {({ active }) => (
                  <button
                    onClick={() => onMarkRead(id)}
                    className={`w-full px-4 py-2.5 text-sm text-left ${
                      active ? 'bg-paper-2 text-ink-strong' : 'text-ink'
                    }`}
                  >
                    {t('dashboard.mark_as_read')}
                  </button>
                )}
              </MenuItem>
            )}
            <MenuItem>
              {({ active }) => (
                <button
                  onClick={() => onDelete(id)}
                  className={`w-full px-4 py-2.5 text-sm text-left ${
                    active ? 'bg-paper-2 text-rose-600' : 'text-rose-600'
                  }`}
                >
                  {t('dashboard.delete')}
                </button>
              )}
            </MenuItem>
          </MenuItems>
        </Menu>
      </div>
    </li>
  );
};

/* ─────────────────────────────── list ────────────────────────────────────── */

const Notifications = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const notifications = useSelector(
    (state) => state.notifications.notifications
  );
  const loading = useSelector((state) => state.notifications.loading);
  const error = useSelector((state) => state.notifications.error);
  const authUser = useSelector((state) => state.auth.user);
  const isBrandUser = authUser?.role === 'brand';

  const latestNotificationsRef = useRef([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewCtx, setReviewCtx] = useState(null);
  const [liveBookingsById, setLiveBookingsById] = useState({});
  const [checkingBookingIds, setCheckingBookingIds] = useState({});
  const [checkedBookingIds, setCheckedBookingIds] = useState({});
  const requestedBookingIdsRef = useRef(new Set());

  useEffect(() => {
    latestNotificationsRef.current = notifications || [];
  }, [notifications]);

  useEffect(() => {
    dispatch(getNotifications());
  }, [dispatch]);

  useEffect(() => {
    if (!notifications?.length) return;

    const idsToVerify = notifications
      .filter((n) => {
        if (n?.type !== 'newBooking') return false;
        const id = n?.bookingId || n?.payload?.bookingId;
        if (!id || requestedBookingIdsRef.current.has(id)) return false;

        const status = getEffectiveBookingStatus({
          ...(n?.payload || {}),
          status:
            n?.bookingStatus ||
            n?.payload?.bookingStatus ||
            n?.payload?.status,
          bookingStatus: n?.bookingStatus || n?.payload?.bookingStatus,
          expireAt:
            n?.expireAt || n?.payload?.expireAt || n?.payload?.expiresAt,
        });

        return !status || status === 'pending';
      })
      .map((n) => n.bookingId || n?.payload?.bookingId);

    idsToVerify.forEach((id) => {
      requestedBookingIdsRef.current.add(id);
      setCheckingBookingIds((prev) => ({ ...prev, [id]: true }));

      dispatch(getBooking(id))
        .then((booking) => {
          setLiveBookingsById((prev) => ({ ...prev, [id]: booking }));
        })
        .catch(() => {
          requestedBookingIdsRef.current.delete(id);
        })
        .finally(() => {
          setCheckedBookingIds((prev) => ({ ...prev, [id]: true }));
          setCheckingBookingIds((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
        });
    });
  }, [dispatch, notifications]);

  const handleMarkAsRead = (id) => dispatch(markNotificationAsRead(id));
  const handleMarkAllAsRead = () => dispatch(markAllNotificationsAsRead());
  const handleDeleteNotification = (id) => dispatch(deleteNotification(id));

  const unreadCount = useMemo(
    () => (notifications || []).filter((n) => !n?.read).length,
    [notifications]
  );

  const handleApproveBooking = async (bookingId) => {
    try {
      const booking = await dispatch(approveBooking(bookingId));
      if (booking) {
        setLiveBookingsById((prev) => ({ ...prev, [bookingId]: booking }));
      }
      await dispatch(getNotifications());
      dispatch(getUnreadNotificationsCount());
      return {
        ok: true,
        at:
          booking?.dateApproved ||
          booking?.updatedAt ||
          new Date().toISOString(),
      };
    } catch (err) {
      return { ok: false, error: err };
    }
  };

  const handleRejectBooking = async (bookingId) => {
    try {
      const booking = await dispatch(rejectBooking(bookingId));
      if (booking) {
        setLiveBookingsById((prev) => ({ ...prev, [bookingId]: booking }));
      }
      await dispatch(getNotifications());
      dispatch(getUnreadNotificationsCount());
      return {
        ok: true,
        at:
          booking?.dateRejected ||
          booking?.updatedAt ||
          new Date().toISOString(),
      };
    } catch (err) {
      return { ok: false, error: err };
    }
  };

  const onViewDetails = ({
    payload,
    bookingId,
    notificationId,
    bookingStatus,
  }) => {
    dispatch(markNotificationAsRead(notificationId));
    setReviewCtx({
      payload,
      bookingId,
      bookingStatus,
      liveBooking: liveBookingsById[bookingId],
    });
    setReviewOpen(true);
  };

  const onPay = (meta) => {
    const qs = new URLSearchParams({
      tab: 'my-events',
      openEventManager: '1',
      emTab: 'booking-status',
      eventId: String(meta.eventId || ''),
      source: 'notifications',
      _t: String(Date.now()),
    }).toString();
    navigate(`/dashboard?${qs}`);
  };

  const onViewEvent = (meta) => {
    if (meta?.eventId) navigate(`/event/${meta.eventSlug}`);
  };

  const list = notifications || [];

  return (
    <div className="space-y-6">
      <TabHeader
        eyebrow={
          unreadCount > 0
            ? t('dashboard.notifications_page.unread_count', {
                count: unreadCount,
              })
            : t('dashboard.notifications_page.all_read')
        }
        title={t('dashboard.notifications_page.title')}
        sub={t('dashboard.notifications_page.subtitle')}
        actions={
          unreadCount > 0 && !loading && !error ? (
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full bg-white text-ink-strong border border-line hover:border-ink/30 text-sm font-bold transition-colors"
            >
              <CheckIcon className="w-4 h-4" />
              {t('dashboard.notif_mark_all_read')}
            </button>
          ) : null
        }
      />

      {/* Filter chips — hidden until notification type grouping is in place
      <div className="flex items-center gap-1.5 mb-1 overflow-x-auto pb-1">
        {[
          ['todas', t('common.all'), list.length],
          ['naolidas', t('dashboard.notifications_page.unread_count', { count: unreadCount }), unreadCount],
        ].map(([id, label, n]) => (
          <button
            key={id}
            className="shrink-0 inline-flex items-center gap-2 h-9 px-4 rounded-full text-[12.5px] font-bold transition-colors bg-white text-muted border border-line hover:text-ink-strong"
          >
            {label}
            <span className="font-mono text-[10.5px] text-muted">{n}</span>
          </button>
        ))}
      </div>
      */}

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <ClipLoader color="#FFBD59" size={36} />
        </div>
      ) : error ? (
        <Card>
          <p className="text-rose-600 text-sm">
            {t('dashboard.error')}
            {error}
          </p>
        </Card>
      ) : list.length === 0 ? (
        <Card>
          <div className="py-10 text-center">
            <div className="w-12 h-12 rounded-2xl bg-brand-softer text-yellow-03 mx-auto mb-3 flex items-center justify-center">
              <BellIcon className="w-6 h-6" />
            </div>
            <h3 className="text-[16px] font-bold text-ink-strong">
              {t('dashboard.notifications_page.empty_title')}
            </h3>
            <p className="text-[13px] text-muted mt-1">
              {t('dashboard.notifications_page.empty_sub')}
            </p>
          </div>
        </Card>
      ) : (
        <Card padded={false}>
          <ul>
            {list.map((n, i) => {
              const notificationBookingId =
                n.bookingId || n?.payload?.bookingId;
              const notificationStatus = getEffectiveBookingStatus({
                ...(n?.payload || {}),
                status:
                  n?.bookingStatus ||
                  n?.payload?.bookingStatus ||
                  n?.payload?.status,
                bookingStatus: n?.bookingStatus || n?.payload?.bookingStatus,
                expireAt:
                  n?.expireAt || n?.payload?.expireAt || n?.payload?.expiresAt,
              });
              const needsFreshCheck =
                n?.type === 'newBooking' &&
                notificationBookingId &&
                !liveBookingsById[notificationBookingId] &&
                !checkedBookingIds[notificationBookingId] &&
                (!notificationStatus || notificationStatus === 'pending');
              const isBookingStateVerified =
                !!liveBookingsById[notificationBookingId] ||
                (notificationStatus && notificationStatus !== 'pending');
              return (
                <NotificationItem
                  key={n.id}
                  {...n}
                  bookingId={notificationBookingId}
                  liveBooking={liveBookingsById[notificationBookingId]}
                  isCheckingBooking={
                    !!checkingBookingIds[notificationBookingId] ||
                    needsFreshCheck
                  }
                  isBookingStateVerified={!!isBookingStateVerified}
                  isBrandUser={isBrandUser}
                  onViewDetails={onViewDetails}
                  onPay={onPay}
                  onMarkRead={handleMarkAsRead}
                  onDelete={handleDeleteNotification}
                  onViewEvent={onViewEvent}
                  t={t}
                  isLast={i === list.length - 1}
                />
              );
            })}
          </ul>
        </Card>
      )}

      <BookingReviewModal
        isOpen={reviewOpen}
        onClose={() => setReviewOpen(false)}
        payload={reviewCtx?.payload}
        bookingId={reviewCtx?.bookingId}
        notificationBookingStatus={
          reviewCtx?.bookingStatus || reviewCtx?.liveBooking?.status
        }
        initialBooking={reviewCtx?.liveBooking}
        onApprove={(id) => handleApproveBooking(id)}
        onReject={(id) => handleRejectBooking(id)}
      />
    </div>
  );
};

export default Notifications;
